import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import HcahpsStars from "@/components/HcahpsStars";
import { Progress } from "@/components/ui/progress";

/**
 * HCAHPS 10-measure breakdown panel for a hospital.
 * Fetches /api/hospitals/{id}/hcahps and renders a card with:
 *  - Summary score + star rating
 *  - Grouped measures (Composites, Individual, Global)
 *  - Per-measure star rating + top-box % score
 *
 * If `data` prop is supplied, no fetch is made (used inside the authed hospital dashboard).
 */
export default function HcahpsPanel({ hospitalId, data: initialData, compact = false }) {
  const [data, setData] = useState(initialData || null);

  useEffect(() => {
    if (initialData) { setData(initialData); return; }
    if (!hospitalId) return;
    (async () => {
      const { data } = await api.get(`/hospitals/${hospitalId}/hcahps`);
      setData(data);
    })();
  }, [hospitalId, initialData]);

  if (!data) {
    return <div className="rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">Loading HCAHPS…</div>;
  }

  const groups = {
    composite: data.measures.filter((m) => m.kind === "composite"),
    individual: data.measures.filter((m) => m.kind === "individual"),
    global: data.measures.filter((m) => m.kind === "global"),
  };

  return (
    <div className="rounded-lg border border-border bg-white" data-testid="hcahps-panel">
      <div className="p-6 border-b border-border">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="overline">HCAHPS · Patient Experience</div>
            <h3 className="mt-1 font-serif text-2xl md:text-3xl text-primary">Hospital Rating</h3>
            <p className="text-xs text-muted-foreground mt-1">
              HCAHPS 10-measure model · Composites · Individual · Global
            </p>
          </div>
          <div className="flex flex-col items-end">
            <HcahpsStars stars={data.summary_stars} size={18} testid="hcahps-summary-stars" />
            <div className="mt-1 text-sm text-muted-foreground">
              Summary <span className="text-primary font-semibold" data-testid="hcahps-summary-score">{data.summary_score}</span>/100
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {[
          { title: "Composite measures", items: groups.composite, testid: "hcahps-composites" },
          { title: "Individual items", items: groups.individual, testid: "hcahps-individual" },
          { title: "Global items", items: groups.global, testid: "hcahps-global" },
        ].map((section) => (
          section.items.length === 0 ? null : (
            <div key={section.title}>
              <div className="overline mb-3">{section.title}</div>
              <div className={compact ? "space-y-3" : "grid grid-cols-1 md:grid-cols-2 gap-4"} data-testid={section.testid}>
                {section.items.map((m) => (
                  <div key={m.key} className="rounded-md border border-border bg-background p-4" data-testid={`hcahps-measure-${m.key}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm font-medium text-primary leading-snug">{m.name}</div>
                      <HcahpsStars stars={m.stars} />
                    </div>
                    <div className="mt-2 flex items-center gap-3">
                      <Progress value={m.score} className="h-1.5 flex-1" />
                      <div className="text-xs text-muted-foreground w-14 text-right">
                        <span className="text-primary font-semibold">{m.score}</span>/100
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        ))}
      </div>

      <div className="px-6 py-3 border-t border-border text-[11px] text-muted-foreground italic">
        Source: {data.source || "HCAHPS 10-measure model"} · {data.policy_version || "HCAHPS 2022"}. Star ratings summarize patient-reported experience only; refer to the Trust Index for a broader multi-criteria view.
      </div>
    </div>
  );
}
